import { FC, useCallback, useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIButton } from '../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import type { ProcessedRow } from '../autoReturnAndResend.types';
import { exportPanelToExcel, formatProcessedDateTime } from '../autoReturnAndResend.helpers';
import { dataGridSx, renderLinkCell } from '../autoReturnAndResend.gridHelpers';
import { AutoReturnAndResendPanelHeader } from './AutoReturnAndResendPanelHeader';
import { ARAR } from '../autoReturnAndResend.classes';

interface AutoReturnAndResendProcessedPanelProps {
    rows: ProcessedRow[];
}

export const AutoReturnAndResendProcessedPanel: FC<AutoReturnAndResendProcessedPanelProps> = ({ rows }) => {
    const { t } = useTranslation();

    const handleExportExcel = useCallback(() => {
        exportPanelToExcel(
            rows,
            [
                { key: 'orderNumber', label: t('general.orderNumber'), resolve: (row) => row.orderNumber ?? '-' },
                { key: 'shippingListNo', label: t('general.shippingNo'), resolve: (row) => row.shippingListNo },
                { key: 'processedBy', label: t('autoReturnAndResend.columns.processedBy'), resolve: (row) => row.processedBy },
                {
                    key: 'processedAt',
                    label: t('autoReturnAndResend.columns.processedAt'),
                    resolve: (row) => formatProcessedDateTime(row.processedAt),
                },
            ],
            'IslemGorenler'
        );
    }, [rows, t]);

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            {
                field: 'orderNumber',
                headerName: t('general.orderNumber'),
                flex: 1,
                minWidth: 130,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(`/orderdetail?OrderNumber=${params.row.orderNumber}`, params.row.orderNumber),
            },
            {
                field: 'shippingListNo',
                headerName: t('general.shippingNo'),
                flex: 1,
                minWidth: 130,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) =>
                    renderLinkCell(`/orderdetail?shippingListNo=${params.row.shippingListNo}`, params.row.shippingListNo),
            },
            {
                field: 'processedBy',
                headerName: t('autoReturnAndResend.columns.processedBy'),
                flex: 1,
                minWidth: 130,
                headerAlign: 'center',
                align: 'center',
            },
            {
                field: 'processedAt',
                headerName: t('autoReturnAndResend.columns.processedAt'),
                flex: 1,
                minWidth: 150,
                headerAlign: 'center',
                align: 'center',
                valueGetter: (_value, row) => formatProcessedDateTime(row.processedAt),
            },
            {
                field: 'status',
                headerName: t('autoReturnAndResend.columns.status'),
                flex: 1,
                minWidth: 120,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params) => (
                    <Chip
                        color="info"
                        size="small"
                        label={t(`autoReturnAndResend.status.${params.row.status}`, {
                            defaultValue: t('autoReturnAndResend.status.queued'),
                        })}
                    />
                ),
            },
        ],
        [t]
    );

    return (
        <Box className={ARAR.panelSection}>
            <AutoReturnAndResendPanelHeader
                actions={
                    <SCUIButton
                        text={t('rpaResender.exportToExcel')}
                        type="secondary"
                        sizeType="default"
                        size="m"
                        disabled={rows.length === 0}
                        onClick={handleExportExcel}
                    />
                }
            />
            <Box className={ARAR.panelGrid} sx={dataGridSx}>
                <SCUICustomDataGrid randomId rows={rows} columns={columns} pageSize={10} autoHeight />
            </Box>
        </Box>
    );
};
